import React, { useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import Fire from "../../api/Fire";
import { useTranslation } from "react-i18next";
import Dialog from "../../components/Dialog";
import Stepper from "react-stepper-horizontal";
import Types from "../../utils/types.json";
import MapComponent from "../../components/MapComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";

const Index = (props) => {
  const { t } = useTranslation();
  const loc = useLocation();
  const [cartItems, setCartItems] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState("");
  const [paymentType, setPaymentType] = useState("Cash");
  const [codeApplied, setCodeApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const deliveryPrice = 10;
  let discount = useRef(0);
  let overall = 0;

  useEffect(() => {
    setCartItems(loc.state.items);
    window.document.title = t("checkout");
  }, [loc, t]);

  //coupon application
  useEffect(() => {
    if (!codeApplied) {
      setCartItems(loc.state.items);
      return;
    }

    if (new Date(codeApplied.endDate) < new Date()) {
      setType(2);
      setMessage(t("codeExpired"));
      setVisible(true);
      setCodeApplied(null);
      return;
    }

    let categories = Types["types-Ar"].filter((cat) => codeApplied.categories.includes(cat.title)).map((cat) => cat.id);

    let old = [...cartItems];
    let dis = 0;
    let newCart = old.map((item) => {
      if (categories.includes(parseInt(item.category))) {
        let amount = item.price * (parseInt(codeApplied.sale) / 100);
        dis += amount;
        return { ...item, price: item.price - amount };
      } else {
        return item;
      }
    });

    discount.current = dis;
    setCartItems(newCart);
  }, [codeApplied]);

  const _applyCode = async () => {
    if (coupon.trim().length === 0) return;
    setLoading((prev) => true);
    try {
      const code = await Fire._getCoupon(coupon.trim());
      if (!code) {
        setType(2);
        setMessage(t("noCode"));
        setVisible(true);
        return;
      }
      setCodeApplied(code);
    } catch (error) {
      setType(2);
      setMessage(error.message);
      setVisible(true);
    } finally {
      setLoading((prev) => false);
    }
  };

  const _handlePayment = async () => {
    setLoading((prev) => true);
    try {
      if (!Fire.currentUser) throw new Error(t("noUser"));
      if (!location || !address) throw new Error(t("pickLocation"));
      let order = {
        user: {
          id: Fire.currentUser.uid,
          phone: Fire.currentUser.photoURL,
          name: Fire.currentUser.displayName,
        },
        paymentType: paymentType,
        status: 0,
        deliveredAt: null,
        items: cartItems.map((item) => {
          let i = {
            id: item.id,
            quantity: item.quantity,
            image: item.image || item.thumbnail,
            price: item.price,
            name: item.name,
          };

          if (item.category) i.category = item.category;
          if (item.description) i.description = item.description;
          if (item.type) i.type = item.type;

          return i;
        }),
        totalPrice: parseFloat((overall + deliveryPrice).toFixed(2)),
        location: location,
        address: address,
      };

      await Fire._submitOrder(order);
      await Fire._updateCart({ items: [] });
      await Fire._updateItems(cartItems);
      setActiveStep(3);
    } catch (error) {
      setType(2);
      setMessage(error.message);
      setVisible(true);
    } finally {
      setLoading((prev) => false);
    }
  };

  const _renderCart = () => {
    return (
      <div className="bg-light rounded p-3 mt-5">
        <div className="text-center">
          <strong>{t("yourItems")}</strong>
        </div>

        {cartItems.map((item, index) => {
          overall += item.price;

          return (
            <div key={index} className="bg-white p-2 rounded d-flex m-2">
              <div className="d-flex justify-content-center align-items-center">
                <img src={item.image || item.thumbnail} alt={item.nameEn} height={50} width={50} />
              </div>

              <div className="mx-3 flex-fill">
                <h5>
                  <strong>{t("language") === "en" ? item.name : item.nameEn}</strong>
                </h5>
                <div className="my-2 mx-1">
                  <span>
                    <strong>{item.price.toFixed(2)}</strong>
                  </span>
                  <span className="text-muted mx-1" style={{ fontSize: 12 }}>
                    {t("language") === "en" ? "ريال سعودي" : "SAR"}
                  </span>
                </div>
              </div>

              <div className="p-2 text-success">
                <strong>{item.type === "kilos" ? item.quantity + " KG" : item.quantity}</strong>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPrevious = () => (
    <button className="btn btn-secondary m-2" onClick={() => setActiveStep((prev) => prev - 1)}>
      {t("previous")}
    </button>
  );
  const renderNext = (isLoc) => (
    <button
      disabled={isLoc && (!location || !address)}
      className="btn btn-primary m-2"
      onClick={() => setActiveStep((prev) => prev + 1)}
    >
      {t("next")}
    </button>
  );

  const renderConfirm = () => (
    <button className="btn btn-success m-2" onClick={_handlePayment}>
      {t("confirm")}
    </button>
  );

  const renderStepOne = () => (
    <Row>
      <Col lg={12}>{_renderCart()}</Col>

      <Col lg={12}>
        <div className="bg-light rounded p-3 mt-5">
          <div className="text-center">
            <strong>{t("coupon")}</strong>
          </div>

          <div className="bg-white p-2 rounded d-flex m-2">
            <input
              className="form-control border-0 flex-fill"
              type="text"
              placeholder={t("coupon")}
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              disabled={codeApplied}
            />
            {loading ? (
              <div className="spinner-grow text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-success"
                style={{ fontSize: 12 }}
                onClick={_applyCode}
                disabled={codeApplied}
              >
                {t("apply")}
              </button>
            )}
          </div>
          {codeApplied ? (
            <div className="d-flex p-2 align-items-center">
              <div className="text-danger flex-fill">
                {`[${codeApplied.sale} %] - ${codeApplied.categories.join(", ")}`}{" "}
              </div>
              <div className="text-primary hoverable" style={{ fontSize: 12 }} onClick={() => setCodeApplied(false)}>
                {t("tryAnother")}
              </div>
            </div>
          ) : null}
        </div>
      </Col>

      <Col lg={12}>
        <div className="bg-light rounded p-3 mt-5">
          <div className="text-center">
            <strong>{t("price")}</strong>
          </div>

          <div className="bg-white p-2 rounded d-flex m-2">
            <div className="flex-fill">{t("deliveryPrice")}</div>
            <div>
              <strong>{deliveryPrice} </strong>
              <span className="text-muted" style={{ fontSize: 12 }}>
                {t("SAR")}
              </span>
            </div>
          </div>

          <div className="bg-white p-2 rounded d-flex m-2">
            <div className="flex-fill">
              {t("subTotal")}{" "}
              <span className="text-muted" style={{ fontSize: 12 }}>
                ({cartItems.length} {t("items")})
              </span>
            </div>
            <div className="text-center">
              <strong>{overall.toFixed(2)} </strong>
              <span className="text-muted" style={{ fontSize: 12 }}>
                {t("SAR")}
              </span>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {t("vat")}
              </div>
            </div>
          </div>

          {codeApplied && (
            <div className="bg-white p-2 rounded d-flex m-2">
              <div className="flex-fill">{t("discount")} </div>
              <div className="text-center">
                <strong className="text-primary">{discount.current.toFixed(2)} </strong>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {t("SAR")}
                </span>
              </div>
            </div>
          )}

          <div className="bg-white p-2 rounded d-flex m-2">
            <div className="flex-fill">{t("grandTotal")}</div>
            <div className="text-center">
              <strong className="text-danger">{(overall + deliveryPrice).toFixed(2)} </strong>
              <span className="text-muted" style={{ fontSize: 12 }}>
                {t("SAR")}
              </span>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {t("vat")}
              </div>
            </div>
          </div>
        </div>
        {renderNext()}
      </Col>
    </Row>
  );

  const renderStepTwo = () => (
    <Row>
      <Col xs={12}>
        <MapComponent
          handleAddress={(place) => {
            setLocation(place.location);
            setAddress(place.formatted_address);
          }}
        />
      </Col>

      {address && (
        <>
          <Col xs={12}>
            <div className="bg-light p-2 rounded">{address}</div>
          </Col>
        </>
      )}

      <div className="d-flex">
        {renderNext(true)}
        {renderPrevious()}
      </div>
    </Row>
  );

  const renderStepThree = () => (
    <Row>
      {loading ? (
        <div className="text-center">
          <div className="spinner-grow text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div>
          <Col xs={12} className="p-3">
            <div className="text-muted mt-3">
              <strong>{t("paymentType")}: </strong>
            </div>

            <div
              className="mt-3 p-2 rounded hoverable"
              style={{ backgroundColor: paymentType === "Cash" ? "rgba(15, 188, 73, 0.3)" : "#F8F9FA" }}
              onClick={() => setPaymentType("Cash")}
            >
              {t("cash")}
            </div>

            <div className="text-muted text-center mt-3">
              <strong>{t("creditUnavailable")}</strong>
            </div>

            {/* <div
              className="mt-3 p-2 rounded hoverable"
              style={{ backgroundColor: paymentType === "Credit Card" ? "rgba(15, 188, 73, 0.3)" : "#F8F9FA" }}
              onClick={() => setPaymentType("Credit Card")}
            >
              {t("creditCard")}
            </div> */}
          </Col>
          <div className="d-flex">
            {renderConfirm()}
            {renderPrevious()}
          </div>
        </div>
      )}
    </Row>
  );

  const renderConfirmation = () => (
    <Row>
      <Col lg={12} className="mt-5 p-5 text-center">
        <FontAwesomeIcon icon={faClipboard} size="10x" color="rgba(15, 188, 73, 0.4)" />
        <div className="mt-5" style={{ color: "rgba(15, 188, 73, 0.9)", fontSize: "24px" }}>
          {t("confirmText")}
        </div>
        <button className="btn btn-success m-5 w-25" onClick={() => props.history.replace("/orders")}>
          {t("ok")}
        </button>
      </Col>
    </Row>
  );

  return (
    <main className="my-5">
      {activeStep !== 3 && (
        <div dir="ltr">
          <Stepper
            steps={[{ title: t("step1") }, { title: t("step2") }, { title: t("step3") }]}
            activeStep={activeStep}
            activeColor="green"
          />
        </div>
      )}

      {
        {
          0: renderStepOne(),
          1: renderStepTwo(),
          2: renderStepThree(),
          3: renderConfirmation(),
        }[activeStep]
      }

      <Dialog visible={visible} onHide={() => setVisible(false)} message={message} type={type} />
    </main>
  );
};

export default Index;
